import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getBusinesses } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { PitchForm } from "@/components/PitchForm";
import { HOME } from "@/constants/testIds";
import { ArrowUpRight, MapPin, Star } from "lucide-react";

const COVER = {
  "sons": "https://images.pexels.com/photos/15898642/pexels-photo-15898642.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  "klassique-barbers": "https://images.pexels.com/photos/1319459/pexels-photo-1319459.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  "angelic-flower-co": "https://images.pexels.com/photos/5566818/pexels-photo-5566818.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  "well-street-kitchen": "https://images.unsplash.com/photo-1560055932-595dab110124?w=1260&h=750&auto=format&fit=crop&q=80",
  "mins-retro": "https://images.unsplash.com/photo-1520006403909-838d6b92c22e?w=1260&h=750&auto=format&fit=crop&q=80",
  "powerup-electrical": "https://images.pexels.com/photos/14319099/pexels-photo-14319099.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
};

const SPAN = ["md:col-span-7 md:row-span-2", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-6", "md:col-span-6"];

const STATIC_BUSINESSES = [
  {
    slug: "sons",
    name: "sons",
    trade: "Specialty Coffee & Bakery",
    borough: "Hackney",
    neighbourhood: "Lower Clapton",
    rating: 4.9,
    reviews: 136,
  },
  {
    slug: "klassique-barbers",
    name: "Klassique Barbers London",
    trade: "Barbershop",
    borough: "Lambeth",
    neighbourhood: "Brixton",
    rating: 4.6,
    reviews: 98,
  },
  {
    slug: "angelic-flower-co",
    name: "Angelic Flower Co.",
    trade: "Florist",
    borough: "Islington",
    neighbourhood: "Angel",
    rating: 5.0,
    reviews: 37,
  },
  {
    slug: "well-street-kitchen",
    name: "Well Street Kitchen",
    trade: "Brunch & All-Day Café",
    borough: "Hackney",
    neighbourhood: "Well Street",
    rating: 4.7,
    reviews: 683,
  },
  {
    slug: "mins-retro",
    name: "Min's Retro & Vintage Clothing Company",
    trade: "Vintage Fashion",
    borough: "Camden",
    neighbourhood: "Buck Street Market",
    rating: 4.7,
    reviews: 3,
  },
  {
    slug: "powerup-electrical",
    name: "24/7 Powerup Electrical Ltd",
    trade: "Emergency Electrician",
    borough: "Wandsworth",
    neighbourhood: "Balham",
    rating: 5.0,
    reviews: 83,
  },
];

const getCoverUrl = (business) => (
  business?.cover_url
  || business?.cover
  || business?.image_url
  || COVER[business?.slug]
);

export default function AgencyHome() {
  const [items, setItems] = useState(STATIC_BUSINESSES);

  useEffect(() => {
    document.title = "The Managerly — London Business Website Redesigns";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "The Managerly rebuilds websites for London's highest-rated independent businesses — cafés, barbers, florists and trades that deserve a better front door online.");
  }, []);

  useEffect(() => {
    let cancelled = false;
    getBusinesses().then((data) => { if (!cancelled && data?.length) setItems(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Nav />

      {/* HERO */}
      <section
        data-testid={HOME.hero}
        className="relative overflow-hidden noise"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(10,10,10,0.4) 0%, rgba(10,10,10,0.85) 60%, #0A0A0A 100%), url(https://images.pexels.com/photos/19153442/pexels-photo-19153442.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&dpr=1)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 md:pt-40 md:pb-48 relative">
          <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.3em] text-white/60 mb-8 rise">
            <span className="block w-6 h-px bg-[#FF3B30]"></span>
            LIVE FROM GOOGLE MAPS · 2026
          </div>

          <h1 className="font-display font-extrabold text-5xl md:text-7xl lg:text-[7.5rem] tracking-tighter leading-[0.92] max-w-5xl rise">
            We rebuild the websites of London's <span className="italic font-light text-[#FF3B30]">best-kept secrets.</span>
          </h1>

          <p className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl rise" style={{ animationDelay: "0.15s" }}>
            We trawled Google Maps across the boroughs, scored thousands of high-street businesses by
            review strength vs web weakness, and redesigned the ones that deserve a better front door.
            Every name on this page is real. So is every star.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-4 text-sm rise" style={{ animationDelay: "0.3s" }}>
            <Stat label="Boroughs scanned" value="11" />
            <Stat label="Listings reviewed" value="340+" />
            <Stat label="Avg. rating, chosen" value="4.8★" />
            <Stat label="With a real website" value={`0 / ${items.length || "—"}`} accent />
          </div>
        </div>

        {/* Marquee */}
        <div className="border-y border-white/10 bg-black/50 overflow-hidden">
          <div className="marquee-track flex whitespace-nowrap py-4 font-mono text-xs tracking-[0.3em] text-white/40">
            {["a", "b", "c", "d"].map((id) => (
              <span key={`marquee-${id}`} className="flex items-center gap-8 pr-8">
                HACKNEY <span className="text-[#FF3B30]">/</span> LAMBETH <span className="text-[#FF3B30]">/</span> CAMDEN <span className="text-[#FF3B30]">/</span> ISLINGTON <span className="text-[#FF3B30]">/</span> TOWER HAMLETS <span className="text-[#FF3B30]">/</span> SOUTHWARK <span className="text-[#FF3B30]">/</span> N1 · E5 · E8 · E9 · SW9 · NW1 <span className="text-[#FF3B30]">/</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 border-b border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-4">01 — Method</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">A scoring engine, not a hunch.</h2>
          </div>
          <div className="md:col-span-8 space-y-6 text-white/70 text-lg leading-relaxed">
            <p>
              We query Google Maps directly for independent trades across the boroughs — cafés, barbers, florists,
              vintage shops, brunch spots — then rank each result on a 0–100 candidacy score.
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
              <Pill k="+35" v="Rating ≥ 4.7" />
              <Pill k="+25" v="Reviews ≥ 500" />
              <Pill k="+40" v="No website at all" />
              <Pill k="+25" v="Instagram-only" />
              <Pill k="+18" v="Wix / GoDaddy stub" />
              <Pill k="+15" v="Reviews ≥ 100" />
            </ul>
            <p className="pt-4">
              The ones below scored highest. Click any of them to see what we'd build them — and the live Google
              listing we pulled it from.
            </p>
          </div>
        </div>
      </section>

      {/* CASE STUDY BENTO GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-4">02 — The Work</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">Real businesses. Redesigned.</h2>
          </div>
          <p className="hidden md:block font-mono text-xs text-white/40">SCROLL · CLICK · BROWSE</p>
        </div>

        <div data-testid={HOME.caseGrid} className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-[280px] gap-4">
          {items.map((b, i) => (
            <Link
              key={b.slug}
              to={`/case/${b.slug}`}
              data-testid={HOME.caseCard(b.slug)}
              className={`group relative overflow-hidden bg-[#141414] border border-white/10 hover:border-[#FF3B30] transition-all duration-500 ${SPAN[i] || "md:col-span-6"}`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center scale-105 group-hover:scale-100 transition-transform duration-700"
                style={{ backgroundImage: `url(${getCoverUrl(b)})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/10" />
              <div className="relative h-full p-6 md:p-8 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
                    0{i + 1} · {b.borough}
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-white/70 group-hover:text-[#FF3B30] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-2">{b.trade}</p>
                  <h3 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-none">{b.name}</h3>
                  <div className="mt-4 flex items-center gap-4 text-xs text-white/60">
                    <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-current text-[#FF3B30]" /> {b.rating} · {b.reviews} reviews</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {b.neighbourhood}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PITCH */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-4">03 — Pitch us</p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-6">
              Own a shop the algorithm forgot?
            </h2>
            <p className="text-white/70 text-lg leading-relaxed">
              Tell us about it. If your reviews speak louder than your website, you're already on our list — we just
              haven't found you yet.
            </p>
          </div>
          <div className="md:col-span-7">
            <PitchForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div>
    <div className={`font-display text-3xl md:text-4xl font-bold ${accent ? "text-[#FF3B30]" : ""}`}>{value}</div>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{label}</div>
  </div>
);

const Pill = ({ k, v }) => (
  <div className="flex items-baseline gap-3 border border-white/10 px-4 py-3">
    <span className="font-mono text-sm text-[#FF3B30] font-bold">{k}</span>
    <span className="text-sm text-white/70">{v}</span>
  </div>
);
